import {GeneratedQuery} from "../types/GeneratedQuery";
import * as APITypes from "../API";

export const listMessagesWithRecipientsAndAttachments = /* GraphQL */ `
  query ListMessagesWithRecipients(
    $filter: ModelMessageFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listMessages(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        bodySnippet
        status
        scheduledDate
        endOfLifeTrigger
        size
        userprofileID
        createdAt
        updatedAt
        Recipients {
          items {
            id
            recipientId
            recipient {
              id
              firstName
              lastName
              email
              phone
              phoneType
              sendSMS
              userprofileID
            }
          }
        }
        Attachments {
          items {
            id
            attachment {
              id
              name
              description
              type
              status
              size
              s3Key
              uploadDate
              userprofileID
            }
          }
        }
      }
      nextToken
      __typename
    }
  }
`;

export const getMessageByIdWithBodyHydrated = /* GraphQL */ `
  query GetMessageByIdWithBodyHydrated($id: ID!) {
    getMessageByIdWithBody(id: $id) {
      id
      title
      body
      bodyIv
      bodyAuthTag
      bodyEncryptedDataKey
      bodySnippet
      status
      scheduledDate
      endOfLifeTrigger
      size
      userprofileID
      Recipients {
        items {
          id
          recipientId
          messageId
          createdAt
          updatedAt
          __typename
          recipient {
            id
            firstName
            lastName
            email
            phone
            phoneType
            sendSMS
            userprofileID
            createdAt
            updatedAt
            __typename
          }
        }
        nextToken
        __typename
      }
      Attachments {
        items {
          id
          messageId
          attachmentId
          createdAt
          updatedAt
          __typename
          attachment {
            id
            name
            description
            type
            status
            size
            s3Key
            uploadDate
            userprofileID
            createdAt
            updatedAt
            __typename
          }
        }
        nextToken
        __typename
      }
      sentDate
      createdAt
      updatedAt
      __typename
    }
  }
` as GeneratedQuery<
  APITypes.GetMessageByIdWithBodyQueryVariables,
  APITypes.GetMessageByIdWithBodyQuery
>;

export const listAttachmentsWithMessages = /* GraphQL */ `query ListAttachmentsWithMessages(
  $filter: ModelAttachmentFilterInput
  $limit: Int
  $nextToken: String
) {
  listAttachments(filter: $filter, limit: $limit, nextToken: $nextToken) {
    items {
      id
      name
      description
      type
      status
      size
      s3Key
      uploadDate
      userprofileID
      messages {
        items {
          message {
            id
            title
            scheduledDate
          }
        }
        nextToken
      }
      createdAt
      updatedAt
    }
    nextToken
  }
}` as GeneratedQuery<
    APITypes.ListAttachmentsQueryVariables,
    APITypes.ListAttachmentsQuery
>;

export const listRecipientsWithMessages = /* GraphQL */ `query ListRecipientsWithMessages(
  $filter: ModelRecipientFilterInput
  $limit: Int
  $nextToken: String
) {
  listRecipients(filter: $filter, limit: $limit, nextToken: $nextToken) {
    items {
      id
      firstName
      lastName
      email
      phone
      phoneType
      sendSMS
      userprofileID
      messages {
        items {
          message {
            id
            title
            scheduledDate
          }
        }
        nextToken
      }
      createdAt
      updatedAt
      __typename
    }
    nextToken
  }
}
` as GeneratedQuery<
    APITypes.ListRecipientsQueryVariables,
    APITypes.ListRecipientsQuery
>;

export const createUserMinimal = /* GraphQL */ `
  mutation CreateUserMinimal($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      status
      billingProviderId
      planStatus
      pendingPlanId
      scheduledPlanId
      userPlanId
      createdAt
      updatedAt
    }
  }
`;

// Creates the caller's User/UserProfile/UserPreferences rows server-side (accountProvisioningHandler),
// with the initial plan chosen by the server. Idempotent.
export const provisionMyAccountDocument = /* GraphQL */ `
  mutation ProvisionMyAccount {
    provisionMyAccount {
      id
      status
      userPlanId
      createdAt
      updatedAt
    }
  }
`;

// The sender's own delivery records (the byFromUserProfileId index; the server only allows querying your own
// id). listDeliveredMessages is scoped to the caller as *recipient*, so it can never serve the sender view.
export const deliveredMessagesByFromUserProfileIdDocument = /* GraphQL */ `
  query DeliveredMessagesByFromUserProfileId(
    $fromUserProfileId: ID!
    $limit: Int
    $nextToken: String
  ) {
    deliveredMessagesByFromUserProfileId(
      fromUserProfileId: $fromUserProfileId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        recipientEmail
        recipientId
        recipientUserId
        deliveredAt
        deliveryType
        messageRead
        readReminderCount
        readReminderStatus
        status
        failureReason
        attemptCount
        lastAttemptAt
        statusUpdatedAt
        fromUserProfileId
        fromName
        fromEmail
        originalMessageId
        title
        body
        bodyIv
        bodyAuthTag
        bodyEncryptedDataKey
        bodySnippet
        hasAttachment
        attachmentCount
        createdAt
        updatedAt
        __typename
      }
      nextToken
      __typename
    }
  }
`;

export const createUserPreferencesDocument = /* GraphQL */ `
  mutation CreateUserPreferencesDocument($input: CreateUserPreferencesInput!) {
    createUserPreferences(input: $input) {
      id
      eolVerificationIntervalDays
      eolVerificationReminderCount
      eolVerificationReminderIntervalDays
      eolVerificationChannelEmailEnabled
      eolVerificationChannelSmsEnabled
      eolStopScheduledMessages
      minBodyWordCount
      eolAccountReaderRecipientId
      preferredLanguage
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const updateUserPreferencesDocument = /* GraphQL */ `
  mutation UpdateUserPreferencesDocument($input: UpdateUserPreferencesInput!) {
    updateUserPreferences(input: $input) {
      id
      eolVerificationIntervalDays
      eolVerificationReminderCount
      eolVerificationReminderIntervalDays
      eolVerificationChannelEmailEnabled
      eolVerificationChannelSmsEnabled
      eolStopScheduledMessages
      minBodyWordCount
      eolAccountReaderRecipientId
      preferredLanguage
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const confirmEolVerificationDocument = /* GraphQL */ `
  mutation ConfirmEolVerificationDocument($token: String!) {
    confirmEolVerification(token: $token) {
      outcome
    }
  }
`;


